import { FC, useState, useCallback } from 'react';
import { FilterLevel, FilterVM, TrackingEventStatus } from '../../../common/view-model';
import './CategoryFilterComponent.scss';
import { handleTracking } from '../../../helpers/handelTracking';
import { getFilterTypeKeyForUrl } from '../../../helpers/urlParametersHelpers';
import { v4 as uuidv4 } from 'uuid';
import { isCurrentUserAnonymous } from '../../../helpers/authenticationHelper';
import { CheckboxComponent } from '../../UI/CheckboxComponent/CheckboxComponent';

interface ICategoryFilterComponentProps {
	filters: FilterVM[];
	selectedFilters?: FilterVM[];
	userState: any;
	setLockModalContent: () => void;
	registerUserClickEvent: (event: string) => void;
	pendingFilters?: FilterVM[];
	setPendingFilters?: React.Dispatch<React.SetStateAction<FilterVM[]>>;
}

export const CategoryFilterComponent: FC<ICategoryFilterComponentProps> = ({
	filters,
	userState,
	setLockModalContent,
	registerUserClickEvent,
	pendingFilters,
	setPendingFilters,
	selectedFilters
}) => {
	const handleTrackingCallback = useCallback(
		(filter: FilterVM) => {
			handleTracking({
				transactionId: uuidv4(),
				status: TrackingEventStatus.SUCCESS,
				entityId: filter.id!,
				type: `Click on ${filter.name}`,
				timeStamp: new Date().toISOString(),
				eventPayload: {
					id: filter.id!,
					name: filter.name!,
					type: getFilterTypeKeyForUrl(filter.type!),
					url: window.location.href,
					tags: [],
					metadata: {
						user_id: userState?.id,
						user_type: userState?.user_type,
						environment: import.meta.env.VITE_ENV_NAME,
						category_id: filter.id,
						category_name: filter.name
					}
				}
			});
		},
		[userState]
	);
	const isAnonymous = isCurrentUserAnonymous();
	const [openCategories, setOpenCategories] = useState<string[]>([]);
	const [openSubCategories, setOpenSubCategories] = useState<{ [key: string]: boolean }>({});
	const toggleCategory = (filterId: string) => {
		setOpenCategories(prev => {
			const exists = prev.includes(filterId);

			return exists ? prev.filter(id => id !== filterId) : [...prev, filterId];
		});
	};
	const handleCategoryToggle = (e: React.MouseEvent, filterId?: string) => {
		e.stopPropagation();

		if (!filterId) return;

		toggleCategory(filterId);
	};
	const toggleSubCategory = (childId: string) => {
		setOpenSubCategories(prev => ({
			...prev,
			[childId]: !prev[childId]
		}));
	};

	const handleSubCategoryToggle = (e: React.MouseEvent, childId?: string) => {
		e.stopPropagation();

		if (!childId) return;

		toggleSubCategory(childId);
	};
	const handleLockClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		setLockModalContent();
	};
	const renderGrandChildren = (child: FilterVM) => {
		if (!child.children?.length) return null;

		return (
			<div className='level3-container tree-level-3'>
				{child.children.map((grandChild: FilterVM, index: number) => (
					<div
						key={grandChild.id}
						className='filter-tooltip-container'
						onClick={() => handleTrackingCallback(grandChild)}
					>
						<CheckboxComponent
							checked={grandChild.isChecked}
							label={grandChild.name}
							style='primary'
							parentIndex={grandChild.id?.toString()}
							index={index}
							level={FilterLevel.THIRD}
							type={grandChild.type}
							parentLength={child.children.length}
							parentId={child.id}
							parentName={child.name}
							isLocked={isAnonymous}
							setLockModalContent={setLockModalContent}
							registerUserClickEvent={registerUserClickEvent}
							pendingFilters={pendingFilters}
							setPendingFilters={setPendingFilters}
							isNewFilter={false}
							isDropdownFilter={true}
							isFirstLevelFilter={false}
							isLowerLevelFilter={true}
							isThirdLevelFilter={true}
						/>
					</div>
				))}
			</div>
		);
	};
	const renderChild = (child: FilterVM, filter: FilterVM) => {
		if (!child.id) return null;

		const isChildOpen = openSubCategories[child.id];

		return (
			<div key={child.id}>
				<button
					className='category-tree_level--2 category-button-reset'
					type='button'
					onClick={() => handleTrackingCallback(child)}
				>
					<CheckboxComponent
						checked={selectedFilters?.some(f => f.id === child.id)}
						label={child.name}
						style='primary'
						parentIndex={child.id?.toString()}
						level={FilterLevel.SECOND}
						type={child.type}
						parentLength={child.children?.length ?? 0}
						isLocked={isAnonymous}
						setLockModalContent={setLockModalContent}
						registerUserClickEvent={registerUserClickEvent}
						pendingFilters={pendingFilters}
						setPendingFilters={setPendingFilters}
						isNewFilter={false}
						isDropdownFilter={true}
						isFirstLevelFilter={false}
						isLowerLevelFilter={true}
						isThirdLevelFilter={false}
					>
						{child.children}
					</CheckboxComponent>

					{isAnonymous ? (
						<button type='button' className='category-icon-button' onClick={handleLockClick}>
							<i className='ri-lock-line' />
						</button>
					) : (
						<button
							type='button'
							className='category-icon-button'
							onClick={e => handleSubCategoryToggle(e, child.id)}
						>
							<i className={isChildOpen ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'} />
						</button>
					)}
				</button>

				{isChildOpen && renderGrandChildren(child)}
			</div>
		);
	};
	const renderFilter = (filter: FilterVM, index: number) => {
		if (!filter.id) return null;

		const isOpen = openCategories.includes(filter.id);

		return (
			<div key={filter.id}>
				<button
					className='category-tree_level--1 category-button-reset'
					type='button'
					onClick={() => handleTrackingCallback(filter)}
				>
					<CheckboxComponent
						checked={selectedFilters?.some(f => f.id === filter.id)}
						label={filter.name}
						style='primary'
						parentIndex={filter.id.toString()}
						level={FilterLevel.FIRST}
						type={filter.type}
						parentLength={filters.length}
						isLocked={isAnonymous}
						setLockModalContent={setLockModalContent}
						registerUserClickEvent={registerUserClickEvent}
						pendingFilters={pendingFilters}
						setPendingFilters={setPendingFilters}
						isNewFilter={false}
						isDropdownFilter={true}
						isFirstLevelFilter={true}
						isLowerLevelFilter={false}
						isThirdLevelFilter={false}
					/>

					{isAnonymous ? (
						<button type='button' className='category-icon-button' onClick={handleLockClick}>
							<i className='ri-lock-line' />
						</button>
					) : (
						<button
							type='button'
							className='category-icon-button'
							onClick={e => handleCategoryToggle(e, filter.id)}
						>
							<i className={isOpen ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'} />
						</button>
					)}
				</button>

				{isOpen && (
					<div className='selected-category-filter-container tree-level-2'>
						{filter.children?.map((child: FilterVM) => renderChild(child, filter))}
					</div>
				)}
				{index !== filters.length - 1 && <div className='category-divider' />}
			</div>
		);
	};

	return (
		<div className='category-filter-container'>
			<div className='category-elements-container'>
				{filters.map((filter, index) => renderFilter(filter, index))}
			</div>
		</div>
	);
};
